import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { api } from '../../services/api';
import LoadingSpinner from '../Shared/LoadingSpinner';
import { fmtMoneyExact, fmtHours, fmtDate } from './format';
import { tbl } from '../../theme';

// Level 3 — employee detail panel. Shows every line item for the scope it was
// opened on: one pay run when the reader had picked one, otherwise the whole
// span the board is on. Each line carries its own check date, so a span is
// simply more rows rather than a different table.
// Pay rate is never requested or shown (the API does not expose it).
export default function EmployeeModal({ empId, periodId, scopeParams = '', filterQ = '', onClose }) {
  const scopeQ = periodId ? `period_id=${periodId}` : scopeParams;
  const { data, isLoading, error } = useQuery({
    queryKey: ['ot-employee', empId, scopeQ, filterQ],
    queryFn:  () => api.get(`/overtime/employee/${encodeURIComponent(empId)}?${scopeQ}${filterQ}`),
    enabled:  !!empId && !!scopeQ,
  });

  const emp   = data?.employee;
  const items = data?.line_items || [];

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={(e) => e.stopPropagation()}>
        <div style={s.header}>
          <div>
            <div style={s.name}>{emp?.employee_name || `Employee ${empId}`}</div>
            <div style={s.sub}>
              #{empId}
              {emp?.job_profile ? ` · ${emp.job_profile}` : ''}
              {emp?.cost_center_name ? ` · ${emp.cost_center_name}` : ''}
              {data?.scope?.label ? ` · ${data.scope.label}` : ''}
            </div>
          </div>
          <button style={s.close} onClick={onClose} title="Close"><X size={18} /></button>
        </div>

        {isLoading ? <div style={s.pad}><LoadingSpinner /></div>
         : error   ? <div style={s.pad}><div style={s.err}>{error.message}</div></div>
         : (
          <>
            <div style={s.summaryRow}>
              <div style={s.summaryItem}><span style={s.sLabel}>OT Hours</span><span style={s.sVal}>{fmtHours(emp?.ot_hours)}</span></div>
              <div style={s.summaryItem}><span style={s.sLabel}>OT Amount</span><span style={s.sVal}>{fmtMoneyExact(emp?.ot_amount)}</span></div>
              {emp?.employment_status && <div style={s.summaryItem}><span style={s.sLabel}>Status</span><span style={s.sVal}>{emp.employment_status}</span></div>}
              {emp?.manager && <div style={s.summaryItem}><span style={s.sLabel}>Manager</span><span style={s.sVal}>{emp.manager}</span></div>}
            </div>

            <div style={s.tableWrap}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Income Type</th>
                    <th style={s.th}>Pay Type</th>
                    <th style={s.th}>Cost Center</th>
                    <th style={s.th}>Check Date</th>
                    <th style={s.thR}>Hours</th>
                    <th style={s.thR}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((li) => (
                    <tr key={li.id} style={{ ...s.tr, ...(li.is_overtime ? s.otRow : {}) }}>
                      <td style={s.td}>
                        {li.income_desc || li.income_code || '—'}
                        {li.is_overtime && <span style={s.otTag}>OT</span>}
                      </td>
                      <td style={s.td}>{li.pay_type || '—'}</td>
                      <td style={s.td}>{li.cost_center_name || li.cost_center_code || '—'}</td>
                      <td style={s.td}>{fmtDate(li.check_date)}</td>
                      <td style={s.tdR}>{fmtHours(li.hours)}</td>
                      <td style={s.tdR}>{fmtMoneyExact(li.amount)}</td>
                    </tr>
                  ))}
                  {!items.length && <tr><td colSpan={6} style={s.empty}>No line items.</td></tr>}
                </tbody>
              </table>
            </div>
            <div style={s.note}>Pay rate is intentionally not shown.</div>
          </>
        )}
      </div>
    </div>
  );
}

const s = {
  overlay:   { position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.55)', zIndex: 200, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '48px 16px', overflowY: 'auto' },
  modal:     { background: '#fff', borderRadius: '14px', width: '100%', maxWidth: '860px', boxShadow: '0 20px 50px rgba(0,0,0,0.25)' },
  header:    { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '20px 22px', borderBottom: '1px solid #E5E7EB' },
  name:      { fontSize: '17px', fontWeight: 700, color: '#111827' },
  sub:       { fontSize: '12px', color: '#6B7280', marginTop: '3px' },
  close:     { background: 'none', border: 'none', color: '#9CA3AF', cursor: 'pointer', padding: '4px', display: 'flex' },
  pad:       { padding: '32px' },
  summaryRow:{ display: 'flex', flexWrap: 'wrap', gap: '28px', padding: '16px 22px', background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' },
  summaryItem:{ display: 'flex', flexDirection: 'column', gap: '2px' },
  sLabel:    { fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#9CA3AF' },
  sVal:      { fontSize: '15px', fontWeight: 700, color: '#111827' },
  tableWrap: { padding: '8px 22px 4px', maxHeight: '46vh', overflowY: 'auto' },
  table:     { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  th:        { ...tbl.th, ...tbl.th, position: 'sticky', top: 0, background: '#fff' },
  thR:       { ...tbl.thR, ...tbl.thR, position: 'sticky', top: 0, background: '#fff' },
  tr:        { ...tbl.tr, ...tbl.tr },
  otRow:     { background: '#FEFCE8' },
  td:        { ...tbl.td, ...tbl.td },
  tdR:       { ...tbl.tdR, ...tbl.tdR },
  otTag:     { marginLeft: '6px', fontSize: '9px', fontWeight: 700, color: '#A16207', background: '#FEF3C7', padding: '1px 5px', borderRadius: '4px', verticalAlign: 'middle' },
  empty:     { textAlign: 'center', padding: '24px', color: '#9CA3AF' },
  err:       { color: '#DC2626', fontSize: '13px' },
  note:      { fontSize: '11px', color: '#9CA3AF', fontStyle: 'italic', padding: '10px 22px 18px' },
};
